// splunkCustomEvents.ts
import { type Attributes, type Span, trace } from '@opentelemetry/api';

export function startCustomEvent(eventName: string, attributes?: Record<string, unknown>): Span {
  const tracer = trace.getTracer('customEvents');
  const span = tracer.startSpan(eventName, {
    attributes: {
      'workflow.name': eventName,
      ...attributes,
    },
  });
  return span;
}

export function trackCustomEvent<T>(
  eventName: string,
  callback: () => T | Promise<T>,
  attributes: Record<string, unknown> = {},
): T | Promise<T> {
  const span = startCustomEvent(eventName, attributes);
  try {
    const result = callback();
    if (result instanceof Promise) {
      return result.finally(() => {
        span.end();
      });
    }

    span.end();
    return result;
  } catch (error: unknown) {
    if (error instanceof Error) {
      span.setAttribute('error.message', error.message);
      throw new Error(error.message);
    } else {
      span.setAttribute('error.message', String(error));
      throw new Error(String(error));
    }
  }
}

export async function trackAsyncEvent<T>({
  eventName,
  attributes,
  asyncCallback,
  onFailure,
  onSuccess,
}: {
  eventName: string;
  attributes: Record<string, unknown>;
  asyncCallback: () => Promise<T>;
  onSuccess?: (result: T) => void;
  onFailure?: (error: unknown) => void;
}): Promise<T> {
  try {
    const result = await trackCustomEvent(eventName, asyncCallback, attributes);
    if (onSuccess) {
      onSuccess(result);
    }

    return result;
  } catch (error) {
    if (onFailure) {
      onFailure(error);
    }

    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(String(error));
    }
  }
}

export function logErrorEvent(
  eventName: string,
  error: unknown,
  attributes: Record<string, unknown> = {},
): void {
  const span = startCustomEvent(eventName, {
    ...attributes,
    error: true,
    'error.message': (error as Error)?.message || String(error),
  });
  span.end();
}

export function logCustomEvent(eventName: string, attributes?: Attributes): void {
  const span = startCustomEvent(eventName, attributes);
  span.end();
}
